import type { Writable } from "svelte/store";
import type { SeafileConfig } from "./fs";
import { FS } from "./fs";
import { FsDir } from "./fsentry";
import type { HttpProgress } from "./http";
import { mkURL } from "./http";
import { throwIf } from "./util";

// The browser cannot call an arbitrary Seafile host. File calls go through
// /api/seafile. A login trades the user and password for an API token. The
// token is a request header, not a query parameter.
// A Seafile library is a top-level folder: a path is /library/dir/file.

class FileSystemSeafile extends FS {
  supportsDirRename = true;

  constructor(config: SeafileConfig) {
    super(config);
  }

  get seafileConfig(): SeafileConfig {
    return this.config as SeafileConfig;
  }

  async readDir(dirPath: string, force: boolean, progress: Writable<any>): Promise<FsDir> {
    throwIf(dirPath == "", "empty dir");
    return this.loadDir(dirPath, force, progress, async () => {
      let entries = await seafileList(this.seafileConfig, dirPath);
      return FsDir.fromWire(dirPath, entries);
    });
  }

  async getPublicURL(path: string): Promise<string> {
    let blob = await this.downloadFile(path, () => {});
    return URL.createObjectURL(blob);
  }

  async downloadFile(path: string, httpProgress: HttpProgress): Promise<Blob> {
    let url = seafileURL(this.seafileConfig, "/api/seafile/download", "path", path);
    let xhr = await seafileSend("GET", url, this.seafileConfig, undefined, httpProgress, "blob");
    return xhr.response as Blob;
  }

  async uploadFile(path: string, file: Blob, httpProgress: HttpProgress): Promise<void> {
    let url = seafileURL(this.seafileConfig, "/api/seafile/upload", "path", path);
    await seafileSend("PUT", url, this.seafileConfig, file, httpProgress);
  }

  async rename(path: string, newPath: string): Promise<void> {
    throwIf(path == newPath, "path must be != newPath");
    let url = seafileURL(this.seafileConfig, "/api/seafile/rename", "path", path, "newPath", newPath);
    await seafileCall(this.seafileConfig, url, "POST");
  }

  async createDirectory(path: string): Promise<void> {
    let url = seafileURL(this.seafileConfig, "/api/seafile/mkdir", "path", path);
    await seafileCall(this.seafileConfig, url, "POST");
  }

  // Seafile keeps deleted entries in the library's trash for a while.
  async deleteFile(path: string): Promise<void> {
    let url = seafileURL(this.seafileConfig, "/api/seafile/delete", "path", path);
    await seafileCall(this.seafileConfig, url, "POST");
  }
}

/**
 * Log in with a user and password. code is the two-factor code, empty for
 * accounts without one. Returns the API token.
 */
export async function seafileLogin(url: string, user: string, password: string, code: string): Promise<string> {
  let headers: Record<string, string> = {
    "X-Seafile-User": encodeSecret(user),
    "X-Seafile-Password": encodeSecret(password),
  };
  if (code) {
    headers["X-Seafile-Code"] = encodeSecret(code);
  }
  let js = await seafileFetch(headers, mkURL("/api/seafile/login", "url", url), "POST");
  if (!js.token) {
    throw new Error("seafile login failed");
  }
  return js.token;
}

async function seafileList(config: SeafileConfig, dir: string): Promise<any[][]> {
  let js = await seafileCall(config, seafileURL(config, "/api/seafile/list", "dir", dir));
  if (!Array.isArray(js)) {
    throw new Error("seafile list failed");
  }
  return js;
}

function seafileURL(config: SeafileConfig, path: string, ...args: string[]): string {
  return mkURL(path, "url", config.url, ...args);
}

function encodeSecret(s: string): string {
  let bytes = new TextEncoder().encode(s);
  let bin = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    bin += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(bin);
}

function seafileHeaders(config: SeafileConfig): Record<string, string> {
  return { "X-Seafile-Token": config.token };
}

function seafileCall(config: SeafileConfig, url: string, method = "GET"): Promise<any> {
  return seafileFetch(seafileHeaders(config), url, method);
}

async function seafileFetch(headers: Record<string, string>, url: string, method: string): Promise<any> {
  let rsp = await fetch(url, { method, headers });
  let text = await rsp.text();
  let js: any = {};
  if (text) {
    try {
      js = JSON.parse(text);
    } catch {
      js = {};
    }
  }
  if (!rsp.ok) {
    throw new Error(js.error || `seafile ${rsp.status}`);
  }
  return js;
}

function seafileSend(
  method: string,
  url: string,
  config: SeafileConfig,
  data: Blob | undefined,
  onProgress: HttpProgress | undefined,
  responseType?: XMLHttpRequestResponseType,
): Promise<XMLHttpRequest> {
  let headers = seafileHeaders(config);
  return new Promise((resolve, reject) => {
    let xhr = new XMLHttpRequest();
    xhr.open(method, url);
    if (responseType) {
      xhr.responseType = responseType;
    }
    for (let [k, v] of Object.entries(headers)) {
      xhr.setRequestHeader(k, v);
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr);
        return;
      }
      seafileErrText(xhr).then(
        (msg) => reject(new Error(msg)),
        () => reject(new Error(`seafile ${xhr.status}`)),
      );
    };
    xhr.onerror = () => {
      reject(new Error("seafile request failed"));
    };
    if (onProgress) {
      let started = Date.now();
      let report = (ev: ProgressEvent) => {
        let total = ev.lengthComputable ? ev.total : 0;
        onProgress(ev.loaded, total, Date.now() - started);
      };
      if (method == "PUT") {
        xhr.upload.onprogress = report;
      } else {
        xhr.onprogress = report;
      }
    }
    if (data) {
      xhr.send(data);
    } else {
      xhr.send();
    }
  });
}

async function seafileErrText(xhr: XMLHttpRequest): Promise<string> {
  let text = "";
  if (xhr.responseType == "blob" && xhr.response instanceof Blob) {
    text = await xhr.response.text();
  } else if (xhr.responseType == "" || xhr.responseType == "text") {
    text = xhr.responseText || "";
  }
  if (text) {
    try {
      let js = JSON.parse(text);
      if (js.error) {
        return String(js.error);
      }
    } catch {
      // keep the status
    }
  }
  return `seafile ${xhr.status}`;
}

export function makeFileSystemSeafile(config: SeafileConfig): FileSystemSeafile {
  return new FileSystemSeafile(config);
}
